import {BaseView} from "@nolimitcity/slot-game/bin/core/base/BaseView";
import {GameResources} from "@nolimitcity/slot-game/bin/core/resource/GameResources";
import {ScalableLayer} from "@nolimitcity/slot-game/bin/core/stage/ScalableLayer";
import {StageManager} from "@nolimitcity/slot-game/bin/core/stage/StageManager";
import {WGGameConfig} from "../../WGGameConfig";
import {TimelineLite, TimelineMax} from "gsap";
import {Helper} from "@nolimitcity/slot-game/bin/core/utils/Helper";
import {WGEngineManager} from "../../engine/WGEngineManager";
import {IReelSymbolStates, WGSpriteSymbol} from "../../engine/Symbol/WGSpriteSymbol";
import {SpineTween} from "@nolimitcity/slot-game/bin/core/animation/SpineTween";
import {IBetLineWinData} from "@nolimitcity/slot-game/bin/core/server/data/IServerData";
import {EventHandler} from "@nolimitcity/slot-game/bin/core/event/EventHandler";
import {SpinEvent} from "@nolimitcity/slot-game/bin/core/spin/event/SpinEvent";
import {IReelSpriteSymbol} from "@nolimitcity/slot-game/bin/core/engines/reel/symbol/IReelSpriteSymbol";
import {WarriorGraveyardGameModuleConfig} from "../../WarriorGraveyardGameModuleConfig";
import {ServerEvent} from "@nolimitcity/slot-game/bin/core/server/event/ServerEvent";
import {GameEvent} from "@nolimitcity/slot-game/bin/core/event/GameEvent";
import {SoundManager} from "../../resource/sound/SoundManager";
import {WGSoundConfig} from "../../WGSoundConfig";

export interface betlineRotate {
    x?: boolean;
    y?: boolean
}

export class BetlinesAndWinbox extends BaseView {
    private _animItems: PIXI.spine.Spine[][] = []; // stores 3*6 columns
    private _layer: ScalableLayer;
    private _spinstarted: boolean = false;
    private _betlineContainer: PIXI.Sprite[] = [];
    private _longestWin: number = 0;


    protected initAnimations(): void {
        this._layer = StageManager.getLayer(WGGameConfig.instance.LAYERS.WIN_FRAMES.name);
        this.createWinBox();
        this.createBetlines();
        this._betlineContainer.forEach((betline: PIXI.Sprite) => {
            this._layer.addChild(betline);
        });
    }

    protected addEventListeners(): void {

        EventHandler.addEventListener(this, SpinEvent.BEFORE_START, () => {
            this._spinstarted = true;
        });
        EventHandler.addEventListener(this, SpinEvent.STOP, () => {
            this._spinstarted = false;
        });
        EventHandler.addEventListener(this, ServerEvent.GAME_DATA_PARSED, (event: GameEvent) => {
            this._longestWin = event.params[0].longestWin;
        });
    }

    /**
     * function to create win box
     */
    private createWinBox(): void {
        for (let i: number = 0; i < 6; i++) {
            this._animItems[i] = [];
            for (let j: number = 0; j < 3; j++) {
                const symbol_anim: PIXI.spine.Spine = new PIXI.spine.Spine(GameResources.getSpineAsset("winbox"));
                symbol_anim.visible = false;
                symbol_anim.width -= 20;
                symbol_anim.skeleton.setToSetupPose();
                symbol_anim.state.tracks = [];
                this._animItems[i].push(symbol_anim);
                this._layer.addChild(symbol_anim);
            }
        }
    }

    /**
     * function to create/draw/position betlines
     * from 1-25 i.e 0 to 24 indexes
     * will storing them in container and changing visibility.
     */
    private createBetlines(): void {
        for (let i: number = 0; i < 26; i++) {
            switch (i) {
                case 0:
                case 1:
                case 2:
                case 3:
                    this.helperForBetlineCreator("betline_1", i - 1);
                    break;
                case 4:
                case 5:
                    this.helperForBetlineCreator("betline_2", 1, {y: i === 5});
                    break;
                case 6:
                case 7:
                    this.helperForBetlineCreator("betline_2", i === 6 ? 0 : 2, {y: i === 6});
                    break;
                case 8:
                case 11:
                    this.helperForBetlineCreator("betline_3", 1, {y: i === 11});
                    break;
                case 9:
                case 10:
                    this.helperForBetlineCreator("betline_3", i === 10 ? 0 : 2, {y: i === 10});
                    break;
                case 12:
                case 13:
                    this.helperForBetlineCreator("betline_4", 1, {y: i === 13});
                    break;
                case 14:
                case 15:
                    this.helperForBetlineCreator("betline_4", 1, {x: true, y: i === 15});
                    break;
                case 16:
                case 17:
                    this.helperForBetlineCreator("betline_5", 1, {x: i === 17});
                    break;
                case 18:
                case 19:
                    this.helperForBetlineCreator("betline_6", 1, {y: i === 19});
                    break;
                case 20:
                case 21:
                    this.helperForBetlineCreator("betline_7", 1, {y: i === 21});
                    break;
                case 22:
                case 23:
                    this.helperForBetlineCreator("betline_8", 1, {y: i === 23});
                    break;
                case 24:
                case 25:
                    this.helperForBetlineCreator("betline_9", 1, {y: i === 25});
                    break;
            }
        }
    }

    /**
     * function to help creation of betline
     * @param betlineName
     * @param symbolId
     * @param rotate
     */
    private helperForBetlineCreator(betlineName: string, symbolId: number, rotate?: betlineRotate) {
        const bt: PIXI.Sprite = new PIXI.Sprite(GameResources.getTextures(betlineName)[0]);
        bt.anchor.set(0.5);
        bt.blendMode = PIXI.BLEND_MODES.SCREEN;
        bt.position.set(bt.width / 2, (symbolId + 1) * 198);
        if (rotate && rotate.x) {
            bt.scale.x *= -1;
        }
        if (rotate && rotate.y) {
            bt.scale.y *= -1;

        }
        bt.visible = false;
        // added this to not show additional betline at 0 index
        if (symbolId < 0) {
            bt.alpha = 0;
        }
        this._betlineContainer.push(bt);

    }
    public playWinBoxAnimationForSymIndiWin(betLineWinData: IBetLineWinData,winAmtCont:PIXI.Container):TimelineMax {
        let duration: number = 0;
        let tl: TimelineMax = new TimelineMax({
            onComplete: () => {
                this.hideAllWinBox();
                this.hideAllBetlines();
            }
        });

        for (let j: number = 0; j < betLineWinData.nrOfSymbols; j++) {
            const reelId:number = j,
                symbolId:number = (betLineWinData).betLine[j];
            const symbol: WGSpriteSymbol = WGEngineManager.getReel(reelId).getSymbol(symbolId) as WGSpriteSymbol;
            this._animItems[reelId][symbolId].position = Helper.copyLocalPosition(this._animItems[reelId][symbolId],
                symbol);
            winAmtCont.position=Helper.copyLocalPosition(winAmtCont,
                this._animItems[reelId][symbolId]);
            // to position winbox for wild symbol bottom and top piece
            if (symbol.isWild) {
                switch (symbol.stateType.state) {
                    case 1:
                        this._animItems[reelId][symbolId].position.y += 15;
                        break;
                    case 3:
                        this._animItems[reelId][symbolId].position.y -= 15;
                        break;
                }
            }
            tl.add(() => {
                this._animItems[reelId][symbolId].visible=true;
                this._animItems[reelId][symbolId].alpha=0;
                this._betlineContainer[(betLineWinData.betLineIndex) + 1].visible = true;
                this._betlineContainer[(betLineWinData.betLineIndex) + 1].alpha=0;
            }, duration);

            tl.to([this._animItems[reelId][symbolId],this._betlineContainer[(betLineWinData.betLineIndex) + 1]],0.5,{alpha:0.3},duration);
        }
        return tl;
    }

    /**
     * function to play win box animation during win presentations
     * @param betLineWinData
     */
    public playWinBoxAnimation(betLineWinData: IBetLineWinData[], winRatioText: string = "large", initialWin: boolean = false) {
        const winRatios = WGGameConfig.instance.WIN_RATIOS.normalWin;
        const windboxAnimName = (winRatioText === winRatios[0].text || winRatioText === winRatios[1].text) ? "winbox_tinywin" : "winbox_largewin";
        let duration: number = 0;
        let tl: TimelineMax = new TimelineMax({
            onUpdate: () => {
                if (this._spinstarted) { // to fix winbox animation playing  while spinning
                    tl.clear(true)
                    this.hideAllWinBox();
                    this.hideAllBetlines();
                }
            }
        });
        for (let i: number = 0; i < betLineWinData.length; i++) {
            for (let j: number = 0; j < betLineWinData[i].nrOfSymbols; j++) {
                let sweeptime; // added to fix #153
                if (initialWin) {
                    sweeptime = 0.3 / this._longestWin;
                } else {
                    sweeptime = 0.3 / betLineWinData[i].nrOfSymbols;

                }
                const reelId = j, symbolId = (betLineWinData[i]).betLine[j];
                const symbol: WGSpriteSymbol = WGEngineManager.getReel(reelId).getSymbol(symbolId) as WGSpriteSymbol;
                this._animItems[reelId][symbolId].position = Helper.copyLocalPosition(this._animItems[reelId][symbolId],
                    symbol);
                // to position winbox for wild symbol bottom and top piece
                if (symbol.isWild) {
                    switch (symbol.stateType.state) {
                        case 1:
                            this._animItems[reelId][symbolId].position.y += 15;
                            break;
                        case 3:
                            this._animItems[reelId][symbolId].position.y -= 15;
                            break;
                    }
                }

                this.handleSymbolState(symbol, reelId, symbolId, j);
                duration = (j + 1) * sweeptime;
                tl.add(() => {
                    new SpineTween(this._animItems[reelId][symbolId], 0, windboxAnimName);
                }, duration);
                tl.add(() => {
                    this._animItems[reelId][symbolId].updateTransform();
                    this._animItems[reelId][symbolId].visible = true;
                    this._betlineContainer[(betLineWinData[i].betLineIndex) + 1].visible = true;
                    // #91
                    if (winRatioText === "Tiny Win" || winRatioText === "Small Win") {
                        this._animItems[reelId][symbolId].alpha = (winRatioText === "Tiny Win") ? 0.65 : 0.8;
                        this._betlineContainer[(betLineWinData[i].betLineIndex) + 1].alpha = (winRatioText === "Tiny Win") ? 0.65 : 0.8;
                    } else {
                        this._animItems[reelId][symbolId].alpha = 1;
                        this._betlineContainer[(betLineWinData[i].betLineIndex) + 1].alpha = 1;
                    }

                }, duration);

                if (!(winRatioText === "Tiny Win") && !(winRatioText === "Small Win")) {
                    tl.add(
                        () => {
                            new SpineTween(this._animItems[reelId][symbolId], 0, "winbox_loop", -1);
                        }, 0.3);
                }
            }
        }
        return tl;
    }

    public hideAllBetlines(): void {
        this._betlineContainer.forEach((betline: PIXI.Sprite) => {
            betline.visible = false;
        });
    }

    /**
     * function to handle symbols states during winbox playing
     * @param symbol
     * @param reelId
     * @param symbolId
     * @param j
     */
    public handleSymbolState(symbol: WGSpriteSymbol, reelId: number, symbolId: number, j: number, jusShow: boolean = false): void {
        if (symbol.isScatter) {
            let scatterIndex = WGEngineManager.getReel(j).scatterSymbols.findIndex((s) => s.posID == symbolId);
            symbol = WGEngineManager.getReel(j)._scatterSymbolContainer.children[scatterIndex] as WGSpriteSymbol;
            jusShow ? symbol.changeState({state: IReelSymbolStates.SHOW}) : symbol.changeState({state: IReelSymbolStates.WIN});
        } else if (symbol.isWild) {
            WarriorGraveyardGameModuleConfig.stackAnimationPlayer.playWildStackAnimation(reelId, "_win");
            WGEngineManager.getReel(reelId).AllWindowSymbols.forEach((symbol: IReelSpriteSymbol, index: number) => {
                jusShow ? symbol.changeState({state: IReelSymbolStates.SHOW}) : symbol.changeState({state: IReelSymbolStates.NORMAL});
                if (index === 1) {
                    WGEngineManager.getReel(reelId).undimWildsOverlay();
                }
            });
        } else if (!symbol.isLowValue) {
            WGEngineManager.getReel(reelId).AllWindowSymbols.forEach((sym: IReelSpriteSymbol) => {
                if (symbol.symName === sym.symName) {
                 sym.changeState({state: IReelSymbolStates.WIN});
                }
            });
        } else {
            jusShow ? symbol.changeState({state: IReelSymbolStates.SHOW}) : symbol.changeState({state: IReelSymbolStates.WIN});
        }
    }

    /**
     * specific function to show winboxes after warrior feature.
     * @param totalNumberOfReels
     */
    public showWinBoxForWarriorFeaturePresentation(reels: number[]): TimelineLite {
        let duration = 0;
        let tl: TimelineLite = new TimelineLite();
        for (let k: number = 0; k < reels.length; k++) {
            for (let j: number = 0; j < WGEngineManager.getReel(reels[k]).windowAmount; j++) {
                let symbol: WGSpriteSymbol = WGEngineManager.getReel(reels[k]).getSymbol(j) as WGSpriteSymbol;
                this._animItems[reels[k]][j].position = Helper.copyLocalPosition(this._animItems[reels[k]][j],
                    symbol);
                // to position winbox for wild symbol bottom and top piece
                if (symbol.isWild) {
                    switch (symbol.stateType.state) {
                        case 1:
                            this._animItems[reels[k]][j].position.y += 15;
                            break;
                        case 3:
                            this._animItems[reels[k]][j].position.y -= 15;
                            break;
                    }
                }
                duration = k * 0.1;
                this._animItems[reels[k]][j].alpha = 1;
                tl.add(() => {
                    new SpineTween(this._animItems[reels[k]][j], 0, "winbox_largewin");
                }, duration);

                tl.add(() => {
                    this._animItems[reels[k]][j].updateTransform();
                    this._animItems[reels[k]][j].visible = true;
                }, duration + 0.01);
                tl.add(
                    () => {
                        new SpineTween(this._animItems[reels[k]][j], 0, "winbox_loop", -1);
                    }, 0.65);
            }
        }
        return tl;
    }

    // function to hide all winbox on abort and other times
    public hideAllWinBox(): void {
        this._animItems.forEach((value: PIXI.spine.Spine[]) => {
            value.forEach((value1: PIXI.spine.Spine) => {
                value1.skeleton.setToSetupPose();
                value1.state.tracks = [];
                value1.alpha = 0;
                value1.visible = false;
            });
        });
    }

    public showAllBetlinesOneByOne(allAtOnce: boolean = false): TimelineLite {
        let duration = allAtOnce ? 0 : 0.035; // duration to fix #117
        let tl: TimelineMax = new TimelineMax();
        tl.add(() => {
            !allAtOnce && SoundManager.playSoundEffect(WGSoundConfig.instance.WARRIOR_BETLINE_SOUND);
        });
        this._betlineContainer.forEach((betline: PIXI.Sprite, index: number) => {
            tl.add(() => {
                betline.visible = true;
            });
            tl.to({}, (index > 20) ? duration / 2 : duration, {});
        });
        return tl;
    }
}